"use client"

import {
  type AnyVariables,
  type UseQueryArgs,
  useQuery,
} from "@opensea/graphql"
import { useAddress, useAuthenticated } from "@/hooks/wallet"

type AuthKeyVariable = {
  _authKey?: string
}

type UseAuthenticatedQueryOptions = {
  throwOnError?: boolean
}

/**
 * Use for queries that require authentication.
 * This handles refetching when the user is authenticated or changes addresses.
 */
export function useAuthenticatedQuery<
  Data = unknown,
  Variables extends AnyVariables = AnyVariables,
>(
  args: UseQueryArgs<Variables, Data>,
  { throwOnError = true }: UseAuthenticatedQueryOptions = {},
) {
  const address = useAddress()
  const authenticated = useAuthenticated()

  const authKey =
    authenticated && address ? `authenticated:${address}` : undefined

  const { variables, pause, ...restArgs } = args

  const shouldPause = Boolean(pause) || !(authenticated && address)

  return useQuery<Data, Variables & AuthKeyVariable>(
    {
      ...restArgs,
      variables: {
        ...variables,
        ...(authKey && { _authKey: authKey }),
      } as Variables & AuthKeyVariable,
      pause: shouldPause,
    },
    { throwOnError },
  )
}
